<template>
  <div>
    <Head :title="`Cobranza - ${client.name}`" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <Link href="/cobranzas" class="text-gray-400 hover:text-orange-500 transition-colors font-bold">
            Cobranzas
          </Link>
          <span class="text-gray-300 font-light">/</span>
          <span>{{ client.name }}</span>
        </h1>
        <p class="text-sm text-gray-500 mt-0.5">{{ client.identification }} · {{ client.phone }} · {{ client.email }}</p>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <a
          :href="`/cobranzas/${client.id}/pdf`" target="_blank"
          class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-file-pdf text-red-600" />
          Estado de Cuenta PDF
        </a>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <p class="text-slate-500 text-sm font-medium mb-1">Total Comprado</p>
        <h3 class="text-3xl font-bold text-slate-800">{{ formatCurrency(stats.total_purchased) }}</h3>
      </div>
      <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <p class="text-slate-500 text-sm font-medium mb-1">Total Pagado</p>
        <h3 class="text-3xl font-bold text-green-600">{{ formatCurrency(stats.total_paid) }}</h3>
      </div>
      <div class="bg-gradient-to-br from-red-500 to-red-600 p-6 rounded-2xl shadow-lg">
        <p class="text-red-100 text-sm font-bold uppercase tracking-wider mb-1">Saldo Pendiente</p>
        <h3 class="text-3xl font-bold text-white">{{ formatCurrency(stats.balance) }}</h3>
        <p v-if="stats.last_payment" class="text-red-100 text-xs mt-2">
          Último pago: {{ formatDate(stats.last_payment.created_at) }} — {{ formatCurrency(stats.last_payment.amount) }}
        </p>
        <p v-else class="text-red-200 text-xs mt-2 italic">Sin pagos registrados</p>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Left: Pending Orders + Register Payment -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Pending Orders -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-50">
            <h2 class="font-semibold text-gray-800">Órdenes con Saldo Pendiente</h2>
          </div>
          <div class="divide-y divide-gray-50">
            <div
              v-for="order in pendingOrders" :key="order.id"
              class="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div>
                <div class="font-semibold text-gray-800">{{ order.number_order || '#' + order.id }}</div>
                <div class="text-sm text-gray-500">{{ formatDate(order.created_at) }}</div>
              </div>
              <div class="text-right">
                <div class="text-base font-bold text-gray-900">{{ formatCurrency(order.total) }}</div>
                <div class="text-sm text-red-600 font-medium">
                  Debe: {{ formatCurrency(order.total - order.balance) }}
                </div>
              </div>
            </div>
            <div v-if="!pendingOrders.length" class="px-6 py-8 text-center text-gray-400 italic text-sm">
              <i class="fa fa-check-circle text-2xl text-green-300 block mb-2" />
              No hay órdenes con saldo pendiente
            </div>
          </div>
        </div>

        <!-- Register Payment Form -->
        <div v-if="pendingOrders.length" class="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div class="px-6 py-4 border-b border-gray-50">
            <h2 class="font-semibold text-gray-800">Registrar Pago</h2>
          </div>
          <form class="px-6 py-5" @submit.prevent="registerPayment">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Orden</label>
                <select v-model="payForm.order_id" class="form-input w-full rounded-xl border-gray-200 text-sm">
                  <option value="">Seleccionar orden...</option>
                  <option v-for="order in pendingOrders" :key="order.id" :value="order.id">
                    {{ order.number_order || 'Orden #' + order.id }} — Debe: {{ formatCurrency(order.total - order.balance) }}
                  </option>
                </select>
                <p v-if="payErrors.order_id" class="text-red-500 text-xs mt-1">{{ payErrors.order_id }}</p>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Monto (USD)</label>
                <input
                  v-model="payForm.amount" type="number" min="0.01" step="0.01"
                  class="form-input w-full rounded-xl border-gray-200 text-sm" placeholder="0.00"
                />
                <p v-if="payErrors.amount" class="text-red-500 text-xs mt-1">{{ payErrors.amount }}</p>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Método de Pago</label>
                <select v-model="payForm.method" class="form-input w-full rounded-xl border-gray-200 text-sm">
                  <option value="">Seleccionar método...</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="zelle">Zelle</option>
                  <option value="pago-movil">Pago Móvil</option>
                </select>
                <p v-if="payErrors.method" class="text-red-500 text-xs mt-1">{{ payErrors.method }}</p>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Referencia</label>
                <input
                  v-model="payForm.reference" type="text"
                  class="form-input w-full rounded-xl border-gray-200 text-sm" placeholder="Número de referencia"
                />
              </div>
            </div>

            <div class="mt-5 flex justify-end">
              <button
                type="submit" :disabled="submitting"
                class="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold transition-all shadow-sm flex items-center gap-2 text-sm disabled:opacity-50"
              >
                <i class="fa fa-check" />
                {{ submitting ? 'Registrando...' : 'Registrar Pago' }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Right: All Orders History -->
      <div class="space-y-6">
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-50">
            <h2 class="font-semibold text-gray-800">Historial de Órdenes</h2>
          </div>
          <div class="divide-y divide-gray-50 max-h-96 overflow-y-auto">
            <div v-for="order in allOrders" :key="order.id" class="px-4 py-3">
              <div class="flex justify-between items-start">
                <div>
                  <div class="text-sm font-semibold text-gray-800">{{ order.number_order || '#' + order.id }}</div>
                  <div class="text-xs text-gray-400">{{ formatDate(order.created_at) }}</div>
                </div>
                <div class="text-right">
                  <div class="text-sm font-bold text-gray-900">{{ formatCurrency(order.total) }}</div>
                  <span
                    :class="getStateClass(order.order_state)"
                    class="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                  >
                    {{ getStateLabel(order.order_state) }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="!allOrders.length" class="px-4 py-6 text-center text-gray-400 italic text-sm">
              Sin historial
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Swal from 'sweetalert2'

export default {
  name: 'CobranzasShow',
  components: { Head, Link },
  layout: Layout,

  props: {
    client: Object,
    pendingOrders: Array,
    allOrders: Array,
    stats: Object,
  },

  data() {
    return {
      submitting: false,
      payErrors: {},
      payForm: {
        order_id: this.pendingOrders?.[0]?.id || '',
        amount: '',
        method: '',
        reference: '',
      },
    }
  },

  methods: {
    registerPayment() {
      this.payErrors = {}
      if (!this.payForm.order_id) { this.payErrors.order_id = 'Selecciona una orden'; return }
      if (!this.payForm.amount || this.payForm.amount <= 0) { this.payErrors.amount = 'Monto inválido'; return }
      if (!this.payForm.method) { this.payErrors.method = 'Selecciona un método'; return }

      this.submitting = true
      this.$inertia.post(`/cobranzas/${this.client.id}/pay`, this.payForm, {
        onSuccess: () => {
          Swal.fire({
            title: '¡Pago registrado!',
            text: 'El pago fue registrado correctamente.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          })
          this.payForm.amount = ''
          this.payForm.reference = ''
        },
        onFinish: () => { this.submitting = false },
      })
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('es-VE', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 2,
      }).format(value || 0)
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('es-VE', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    },

    getStateLabel(state) {
      const map = { finish: 'Finalizada', request: 'Pendiente', cancel: 'Anulada', pay: 'Pagada', delivery: 'Entrega' }
      return map[state] || state
    },

    getStateClass(state) {
      const map = {
        finish: 'bg-green-100 text-green-700',
        cancel: 'bg-red-100 text-red-700',
        request: 'bg-yellow-100 text-yellow-700',
        pay: 'bg-blue-100 text-blue-700',
        delivery: 'bg-purple-100 text-purple-700',
      }
      return map[state] || 'bg-gray-100 text-gray-700'
    },
  },
}
</script>
